/**
 * CiteLinks - shared repository deep-link badges for CSK demos.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { SafeLink } from "./SafeLink.js";

const DEFAULT_REPO_URL = "https://github.com/InvariantHoldings/categorical-safety-kernel";
const DEFAULT_BRANCH = "main";

export interface CiteLinkItem {
  readonly label: string;
  readonly path: string;
}

export interface CiteLinksTheme {
  readonly muted?: string;
  readonly mono?: string;
}

export interface CiteLinksProps {
  readonly items: readonly CiteLinkItem[];
  readonly repoUrl?: string;
  readonly branch?: string;
  readonly label?: string;
  readonly ariaLabel?: string;
  readonly tone?: string;
  readonly theme?: CiteLinksTheme;
  readonly style?: React.CSSProperties;
  readonly labelStyle?: React.CSSProperties;
  readonly linkStyle?: React.CSSProperties;
}

function normalizeRepoPath(path: string): string {
  const segments: string[] = [];
  for (const segment of path.trim().replace(/^\/+/, "").split("/")) {
    if (segment.length === 0 || segment === ".") continue;
    if (segment === "..") {
      if (segments.length === 0) return "";
      segments.pop();
      continue;
    }
    segments.push(segment);
  }
  return segments.map(encodeURIComponent).join("/");
}

function encodeRepoRef(ref: string): string {
  return encodeURIComponent(ref).replaceAll("%2F", "/");
}

/**
 * Strip trailing slashes from a string with a single linear scan.
 *
 * The previous `replace(/\/+$/, "")` is polynomial under adversarial
 * input on some regex engines because the greedy `+` retries every
 * start position; this loop scans from the end and stops at the first
 * non-slash byte.
 */
function trimTrailingSlashes(value: string): string {
  let end = value.length;
  while (end > 0 && value.charCodeAt(end - 1) === 0x2f /* '/' */) {
    end -= 1;
  }
  return end === value.length ? value : value.slice(0, end);
}

export function repoBlobHref(
  path: string,
  repoUrl: string = DEFAULT_REPO_URL,
  branch: string = DEFAULT_BRANCH,
): string {
  const repo = trimTrailingSlashes(repoUrl.trim()) || DEFAULT_REPO_URL;
  const ref = encodeRepoRef(branch.trim() || DEFAULT_BRANCH);
  const repoPath = normalizeRepoPath(path);
  return repoPath.length > 0 ? `${repo}/blob/${ref}/${repoPath}` : repo;
}

export function CiteLinks({
  items,
  repoUrl = DEFAULT_REPO_URL,
  branch = DEFAULT_BRANCH,
  label = "cite",
  ariaLabel = "Open in repository",
  tone = "#38bdf8",
  theme,
  style,
  labelStyle,
  linkStyle,
}: CiteLinksProps): React.JSX.Element {
  return (
    <nav
      aria-label={ariaLabel}
      style={{
        alignItems: "center",
        display: "flex",
        flexWrap: "wrap",
        gap: 6,
        ...style,
      }}
    >
      <span
        style={{
          alignSelf: "center",
          color: theme?.muted ?? "#64748b",
          fontFamily: theme?.mono ?? "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: 0,
          marginRight: 4,
          textTransform: "uppercase",
          ...labelStyle,
        }}
      >
        {label}
      </span>
      {items.map((item) => (
        <SafeLink
          key={`${item.label}:${item.path}`}
          href={repoBlobHref(item.path, repoUrl, branch)}
          style={{
            background: `${tone}14`,
            border: `1px solid ${tone}55`,
            borderRadius: 999,
            color: tone,
            fontFamily: theme?.mono ?? "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0,
            padding: "2px 8px",
            textDecoration: "none",
            ...linkStyle,
          }}
        >
          {item.label}
        </SafeLink>
      ))}
    </nav>
  );
}
